import React, { useEffect, useState } from 'react';
import DateInput from '../../ui/DateInput';
import { api } from '../../../lib/api';

// Generic schema-driven renderer for project transition forms. Mirrors
// TransitionFormFields.jsx (account stages) but adds the multi_select control
// for the media_subtypes case. Sprint C ships all 7 forward forms through
// this single renderer.

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-gray-600';
const labelCls = 'block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5';

// Active Media-role users, the same list the paid-shoot assign dialog uses.
// The field used to be a free-text box expecting a user id: a typed name
// looked assigned but the calendar hold silently failed on its foreign key.
function MediaUserSelect({ id, tid, value, onChange }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    const p = api.get('/api/admin/users?role=media');
    if (!p || typeof p.then !== 'function') return undefined;
    p.then((d) => { if (alive) setUsers((d?.users || []).filter((u) => u.isActive !== false)); })
      .catch((err) => { if (alive) { setUsers([]); setError(err?.message || 'Could not load media staff'); } });
    return () => { alive = false; };
  }, []);
  const known = users?.some((u) => u.id === value);
  return (
    <div>
      <select id={id} data-testid={tid} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={!users} className={inputCls}>
        <option value="">{users ? 'Unassigned' : 'Loading media staff…'}</option>
        {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name}{u.email ? ` · ${u.email}` : ''}</option>)}
        {value && users && !known && <option value={value}>Unknown user ({value})</option>}
      </select>
      {users && users.length === 0 && !error && <p className="mt-1 text-[11px] text-amber-300">No active media-role users yet. Add one under Users with the Media role.</p>}
      {error && <p className="mt-1 text-[11px] text-red-400">{error}</p>}
    </div>
  );
}

function FieldLabel({ field }) {
  return (
    <span className={labelCls}>
      {field.label}
      {field.required && <span className="text-red-400/80 ml-0.5">*</span>}
    </span>
  );
}

function MultiSelect({ field, value, onChange, dataTestid }) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={dataTestid}>
      {field.options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          data-testid={`${dataTestid}-option-${opt}`}
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
            arr.includes(opt)
              ? 'bg-[#B0FC38]/15 border-[#B0FC38]/30 text-white'
              : 'bg-white/[0.02] border-white/[0.08] text-gray-400 hover:text-white'
          }`}
        >
          {opt.replace(/_/g, ' ')}
        </button>
      ))}
    </div>
  );
}

// Duration options are hours; everything else is a snake_case enum.
function selectLabel(field, opt) {
  if (field.key === 'shoot_duration_hours') return `${opt} hour${opt === '1' ? '' : 's'}`;
  return String(opt).replace(/_/g, ' ');
}

function Field({ field, value, onChange, error }) {
  const id = `pfield-${field.key}`;
  const tid = `pfield-${field.key}`;
  const renderControl = () => {
    switch (field.type) {
      case 'multi_select':
        return <MultiSelect field={field} value={value} onChange={onChange} dataTestid={tid} />;
      case 'select':
        return (
          <select id={id} data-testid={tid} value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {field.options.map((opt) => (
              <option key={opt} value={opt}>{selectLabel(field, opt)}</option>
            ))}
          </select>
        );
      case 'date':
        return <DateInput id={id} data-testid={tid} value={value || ''} onChange={onChange} className={inputCls} />;
      case 'datetime':
        return <DateInput id={id} data-testid={tid} withTime value={value || ''} onChange={onChange} className={inputCls} />;
      case 'currency':
        return <input id={id} data-testid={tid} type="number" inputMode="numeric" min="0" value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="0" className={inputCls} />;
      case 'checkbox':
        return (
          <label className="inline-flex items-center gap-2 text-[13px] text-gray-200">
            <input id={id} data-testid={tid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[#B0FC38]" />
            <span>{field.label}</span>
          </label>
        );
      case 'media_user':
        return <MediaUserSelect id={id} tid={tid} value={value} onChange={onChange} />;
      case 'text':
      default:
        return <input id={id} data-testid={tid} type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
    }
  };
  return (
    <div>
      {field.type !== 'checkbox' && <label htmlFor={id}><FieldLabel field={field} /></label>}
      {renderControl()}
      {error && <p className="mt-1 text-[11px] text-red-400" data-testid={`${tid}-error`}>{error}</p>}
    </div>
  );
}

export default function ProjectTransitionFields({ schema, values, onChange, errors }) {
  if (!schema?.fields) return null;
  return (
    <div className="space-y-4">
      {schema.fields.map((field) => (
        <Field
          key={field.key}
          field={field}
          value={values?.[field.key]}
          onChange={(v) => onChange({ ...values, [field.key]: v })}
          error={errors?.[field.key]}
        />
      ))}
    </div>
  );
}
